import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Self-hosted fonts (CSP-clean, precached by the service worker).
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import '@/theme/tokens.css';
// Side-effect import, and it must stay above App: Chrome fires
// `beforeinstallprompt` before React mounts, so the listener has to exist by
// the time the bundle finishes evaluating or the offer is lost silently.
import '@/lib/installPrompt';
import { App } from '@/App';
import { listenForOpenAlert, registerServiceWorker } from '@/pwa';
import { useStore } from '@/store';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Shop-floor data is polled; failures surface rather than retry forever.
      retry: 1,
      refetchOnWindowFocus: true,
      refetchIntervalInBackground: false,
      staleTime: 10_000,
    },
    mutations: { retry: 0 },
  },
});

// The worker: a precached shell, an offline front door, and — with the andon
// preview on — pushes. Updates wait for a yes (see `pwa.ts`); this is where the
// yes is asked for.
registerServiceWorker(() => useStore.getState().setUpdateReady(true));

// A tapped push notification asks the open tab to jump to that andon. With no
// tab open the worker opens /alert/<id> instead, which the router lands on —
// same destination either way. Every session has the Andon tab to show the
// sheet over; which sheet — the thread, or the read-only one — is the
// shell's call, the same as for the URL.
listenForOpenAlert((alertId) => {
  const store = useStore.getState();
  store.setTab('andon');
  store.openAndon(alertId);
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
